import { CSSProperties, useState } from "react";
import CardSymphony from "elements/CardSymphony";
import WebsiteBuilderHeader from "../WebsiteBuilderHeader";
import { calculateThemeColors, clsx, styx } from "modules/Utils";
import ButtonSymphony from "elements/ButtonSymphony";
import useWebsiteBuilder, { hasMusicPreview, MusicContentStyle, WebsiteBuilder_musicExists } from "Hooks/useWebsiteBuilder";
import ModalSymphony from "elements/ModalSymphony";
import RecordSearchView from "elements/RecordSearchView";
import { RecordDetails } from "modules/Types";
import useScreen from "Hooks/useScreen";
import DebugBox from "components/shareable/DebugBox";
import { getPalette } from "react-palette";
import Sty from "modules/Sty";
import OptionsSymphony, { OptionItem } from "elements/OptionsSymphony";
import WebsiteBuilderEmptyState from "../WebsiteBuilderEmptyState";
import WebsiteBuilderFooter from "../WebsiteBuilderFooter";
import Colors from "modules/Colors";
import Icon from "elements/Icon";
import { FullImageWebsiteThemeIcon, VinylWebsiteThemeIcon } from "components/svg-icons/website/themes/icons";
import DefaultWebsiteThemeIcon from "components/svg-icons/website/themes/default";
import Banner from "elements/Banner";

interface MusicContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const MusicContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: MusicContentProps) => {
    const { cssCalc, mobileView } = useScreen();
    const {
        websiteContent,
        setWebsiteContent,
        websiteStyle,
        setWebsiteStyle,
    } = useWebsiteBuilder() as WebsiteBuilder_musicExists;

    const musicContent = websiteContent[contentIndex].musicContent;
    const musicContentStyle = musicContent.style;
    const selectedMusic = musicContent.selectedMusic;

    const requiresPaidPlan = musicContentStyle === MusicContentStyle.RECORD_PLAYER || musicContentStyle === MusicContentStyle.FULL_SCREEN;

    const MUSIC_STYLE_TYPES: OptionItem[] = [
        {
            id: MusicContentStyle.PLAYER,
            label: 'Player',
            childrenLeft: (
                <FullImageWebsiteThemeIcon
                    selected={musicContentStyle === MusicContentStyle.PLAYER}
                    width={30}
                    height={30}
                />
            ),
        },
        {
            id: MusicContentStyle.ARTWORK,
            label: 'Artwork',
            childrenLeft: (
                <DefaultWebsiteThemeIcon
                    selected={musicContentStyle === MusicContentStyle.ARTWORK}
                    width={30}
                    height={30}
                />
            ),
        },
        {
            id: MusicContentStyle.RECORD_PLAYER,
            label: 'Record Player',
            iconRight: 'gem',
            childrenLeft: (
                <VinylWebsiteThemeIcon
                    selected={musicContentStyle === MusicContentStyle.RECORD_PLAYER}
                    width={30}
                    height={30}
                />
            ),
        },
        {
            id: MusicContentStyle.FULL_SCREEN,
            label: 'Full Screen',
            iconRight: 'gem',
            childrenLeft: (
                <Icon
                    className="musicContentFullScreenIcon"
                    style={{
                        marginLeft: -4,
                        marginRight: -4,
                    }}
                    icon="expand"
                    size={30}
                    color={musicContentStyle === MusicContentStyle.FULL_SCREEN ? Colors.purple : Colors.greyDark}
                />
            ),
        },
    ]

    const [showRecordSelectorModal, setShowRecordSelectorModal] = useState<boolean>(false);

    const handleSelectMusic = async (recordDetails: RecordDetails) => {
        const image = recordDetails.thumbnail_url;

        const updatedContent = [...websiteContent];
        updatedContent[contentIndex].musicContent.selectedMusic = recordDetails;
        updatedContent[contentIndex].musicContent.title = musicContent.title;

        // console.log('MusicContent - recordDetails >>>>', recordDetails);

        setWebsiteContent(updatedContent);
        setShowRecordSelectorModal(false);

        if (contentIndex === 0) {
            const paletteData = await getPalette(image);
            const {
                backgroundColor,
                secondaryColor,
                textColor
            } = calculateThemeColors(paletteData);

            setWebsiteStyle({
                ...websiteStyle,
                backgroundColor,
                secondaryColor,
                textColor,
            });
        }
    }

    const renderHeader = () => {
        return (
            <WebsiteBuilderHeader
                contentIndex={contentIndex}
                rightSideContent={
                    <>
                        {tabSelectorComponent}
                    </>
                }
            />
        )
    }

    const renderSelectedMusic = () => {
        if (!selectedMusic) {
            return (
                <WebsiteBuilderEmptyState
                    className="musicContentSelectedMusicEmptyState"
                    style={sty.musicContentSelectedMusicEmptyState}
                    title="Showcase your music releases."
                    subtitle="You can add links + displays of your songs, albums, and playlists."
                    isHorizontal
                    buttonText="Select a release"
                    onClickButton={() => setShowRecordSelectorModal(true)}
                />
            );
        }

        const selectedMusicLocal = musicContent.selectedMusic!;

        const {
            name,
            thumbnail_url,
            primary_artist,
            record_url,
            spotify_release_type,
            tracks
        } = selectedMusicLocal;

        return (
            <div
                className="musicContentSelectedMusicContainer"
                style={sty.musicContentSelectedMusicContainer}
            >
                <img
                    className="musicContentSelectedMusicImage"
                    style={sty.musicContentSelectedMusicImage}
                    src={thumbnail_url}
                    alt={name}
                />
                <div className="musicContentSelectedMusicInfo">
                    <div
                        className="musicContentSelectedMusicTitle"
                        style={sty.musicContentSelectedMusicTitle}
                    >
                        {name}
                    </div>
                    <div
                        className="musicContentSelectedMusicArtist"
                        style={sty.musicContentSelectedMusicArtist}
                    >
                        {primary_artist} – {spotify_release_type}
                    </div>
                </div>

                {/* <div
                    className="musicContentSelectedMusicTracks">
                    {tracks?.map((track, index) => {
                        return (
                            <div
                                key={makeKey(track.name, index, contentIndex)}
                            >
                                {track.name}
                            </div>
                        )
                    })}
                </div> */}
                <ButtonSymphony
                    className="musicContentSelectedMusicButton"
                    style={{
                        marginLeft: 'auto',
                    }}
                    variant="text"
                    onClick={() => setShowRecordSelectorModal(true)}
                >
                    Change
                </ButtonSymphony>
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <CardSymphony
                className={clsx("musicContentComponent", className)}
                style={styx(sty.musicContentComponent)}
                size="small"
                id={id}
            >
                {renderHeader()}
                {/* <DebugBox
                    alwaysShow
                    data={{
                        musicContent
                    }}
                /> */}
                {renderSelectedMusic()}
                {selectedMusic && !hasMusicPreview(selectedMusic) &&
                    <Banner
                        className="musicContentNoPreviewBanner"
                        title="No Music Preview"
                        description="This release does not have a music preview, please select a different release. Some releases on Spotify do not have a preview, you can contact your distro or label for more information."
                        color="yellow"
                    />
                }
                <WebsiteBuilderFooter
                    className="musicContentFooter"
                    contentIndex={contentIndex}
                    requiresPaidPlan={requiresPaidPlan}
                    isBackgroundColor
                    isTextColor
                    titleRequired="This music style requires a paid plan to publish your website."
                    styleOptions={
                        <OptionsSymphony
                            className="musicContentStyleOptions"
                            style={{ flexWrap: 'wrap' }}
                            options={MUSIC_STYLE_TYPES}
                            value={musicContentStyle || ''}
                            handleOptionSelected={(option) => {
                                const updatedContent = [...websiteContent];
                                updatedContent[contentIndex].musicContent.style = option as MusicContentStyle;
                                setWebsiteContent(updatedContent);
                            }}
                            size="small"
                            isRadio={false}
                            childrenLeftOffset={-4}
                        />
                    }
                />
            </CardSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="musicContentRecordSelectorModal"
                isOpen={showRecordSelectorModal}
                onClose={() => setShowRecordSelectorModal(false)}
                title="Search Music"
                subtitle="Search for music to add to your website."
            >
                <RecordSearchView
                    handleSelectRecord={handleSelectMusic}
                    // options={['single', 'project', 'playlist']}
                    options={['single']}
                    letUserSearchSpotify
                    letUserRefreshReleases
                    maxHeight={cssCalc(64 + 340)}
                />
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    musicContentComponent: {
    },
    musicContentSelectedMusicContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        border: Sty.border,
        borderRadius: 16,
    },
    musicContentSelectedMusicInfo: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
    },
    musicContentSelectedMusicImage: {
        width: 75,
        height: 75,
        borderRadius: 8,
        overflow: 'hidden',
    },
    musicContentSelectedMusicTitle: {
        ...Sty.text18,
    },
    musicContentSelectedMusicArtist: {
        ...Sty.text14grey,
    },
    musicContentSelectedMusicType: {
        ...Sty.text14grey,
    },

}

export default MusicContent;